import MDEditor from "@uiw/react-md-editor";
import React, { useContext, useEffect, useState } from "react";
import { setItemEditing } from "../../api/ItemsApi";
import { Item } from "../../data/ItemOfBusiness";
import { SLinkContext } from "../../state/context";
import { ItemEditor } from "../../state/state";
import { markdownDiff } from "../diff/markdownDiff";

/** Open editors re-announce themselves this often... */
export const PRESENCE_REFRESH_MS = 60_000;
/** ...and are forgotten if not heard from for this long (e.g. a closed laptop). */
export const EDITOR_EXPIRY_MS = 150_000;

/** While subId is set, tells other organisers "I'm editing this item" (refreshed every minute). */
export function useEditingPresence(subId: string | null | undefined) {
  useEffect(() => {
    if (!subId) return;
    setItemEditing(subId, true);
    const id = setInterval(() => setItemEditing(subId, true), PRESENCE_REFRESH_MS);
    const onUnload = () => setItemEditing(subId, false);
    window.addEventListener("beforeunload", onUnload);
    return () => {
      clearInterval(id);
      window.removeEventListener("beforeunload", onUnload);
      setItemEditing(subId, false);
    };
  }, [subId]);
}

/** Other people (not me) with a live editor open on the item. */
export function activeEditors(editors: ItemEditor[], subId: string | null | undefined, myUserId: string | undefined, now: number): ItemEditor[] {
  if (!subId) return [];
  return editors.filter((e) => e.subId === subId && e.userId !== myUserId && now - e.at < EDITOR_EXPIRY_MS);
}

/** "Jane Smith", "Jane Smith and Bob Lee", "Jane Smith, Bob Lee and Ann Wu" */
export function editorNames(editors: ItemEditor[]): string {
  const names = editors.map((e) => e.name || "Someone");
  return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

/** Other people editing subId now; re-evaluated every 30s so stale entries drop off. */
export function useOtherEditors(subId: string | null | undefined): ItemEditor[] {
  const { state } = useContext(SLinkContext);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  return activeEditors(state.itemEditors, subId, state.user?.userId, Math.max(now, Date.now()));
}

/** "Jane Smith is also editing this item." (renders nothing when nobody else is) */
export function OtherEditorsNotice({ subId, what = "this item" }: { subId: string | null | undefined; what?: string }) {
  const editors = useOtherEditors(subId);
  if (editors.length === 0) return null;
  return (
    <div className="alert alert-info py-2" role="status">
      <strong>{editorNames(editors)}</strong> {editors.length === 1 ? "is" : "are"} also editing {what}.
    </div>
  );
}

function formatTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d.getTime()) || d.getFullYear() < 2000 ? "" : ` at ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

export interface ItemConflictBannerProps {
  /** The item as it is now; null if it was deleted. */
  latest: Item | null;
  /** The text in this editor, to compare with the latest. */
  yourText: string;
  /** Server's reason, e.g. "being voted on" (otherwise a generic "updated by" message). */
  reason?: string;
  onLoadLatest: () => void;
}

/**
 * Shown when the item being edited has been changed by someone else. Saving would be
 * rejected; the editor loads the latest version and re-applies their change.
 */
export function ItemConflictBanner({ latest, yourText, reason, onLoadLatest }: ItemConflictBannerProps) {
  const [showDiff, setShowDiff] = useState(false);
  if (latest === null) {
    return (
      <div className="alert alert-danger" role="alert">
        <strong>This item has been deleted</strong> by someone else, so your changes can't be saved. Copy any text you need, then cancel.
      </div>
    );
  }
  const message = reason || `${latest.updatedBy || "Someone"} updated this item${formatTime(latest.updatedAt)}.`;
  return (
    <div className="alert alert-warning" role="alert">
      <div>
        <strong>{message}</strong> Your changes haven't been saved. Load the latest version, then re-apply your change.
      </div>
      <div className="mt-2 d-flex flex-wrap gap-2">
        <button type="button" className="btn btn-warning btn-sm" onClick={onLoadLatest}>
          Load latest version
        </button>
        <button type="button" className="btn btn-light btn-sm" onClick={() => setShowDiff(!showDiff)}>
          {showDiff ? "Hide differences" : "Show differences"}
        </button>
      </div>
      {showDiff ? (
        <div className="mt-2 p-2 bg-white border rounded sl-prose" data-color-mode="light">
          <div className="small text-muted mb-1">Your text compared with the latest version (insertions are yours, deletions are theirs):</div>
          <MDEditor.Markdown source={markdownDiff(latest.text, yourText, "Word")} />
        </div>
      ) : null}
    </div>
  );
}

/** After "Load latest version": the editor's previous text, kept visible so it can be re-applied. */
export function PreviousDraft({ text, onDismiss }: { text: string; onDismiss: () => void }) {
  return (
    <div className="alert alert-light border" role="note">
      <div className="d-flex align-items-center mb-1">
        <strong>Your previous text</strong>
        <span className="text-muted small ms-2">copy what you need into the latest version</span>
        <button type="button" className="btn btn-light btn-sm ms-auto" onClick={onDismiss}>
          Dismiss
        </button>
      </div>
      <textarea className="form-control font-monospace small" readOnly rows={6} value={text} aria-label="Your previous text" />
    </div>
  );
}
